import React, { useState } from 'react';
import { useApiTokens, ApiToken, CreatedToken } from '../hooks/useApiTokens';

/**
 * "API Tokens" card for the Settings page. Lists tokens, lets the user create
 * a new one (raw value shown exactly once), and revoke existing ones.
 *
 * The PAT is the credential the Sente CLI uses. Tokens are bound to one
 * organization at issuance time and inherit the creator's role at use time.
 */
const ApiTokensSection: React.FC = () => {
  const { tokens, isLoading, error, refresh, createToken, revokeToken } = useApiTokens();
  const [showCreate, setShowCreate] = useState(false);
  const [newTokenName, setNewTokenName] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [justCreated, setJustCreated] = useState<CreatedToken | null>(null);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTokenName.trim()) return;
    setCreating(true);
    setCreateError(null);
    try {
      const result = await createToken(newTokenName.trim());
      setJustCreated(result);
      setNewTokenName('');
      setShowCreate(false);
    } catch (err: any) {
      const code = err?.response?.data?.code;
      if (code === 'API_TOKENS_DISABLED') {
        setCreateError(
          'API tokens are disabled for this organization. An owner can re-enable them in Organization Settings.',
        );
      } else {
        setCreateError(err?.response?.data?.error ?? err?.message ?? 'Failed to create token');
      }
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (id: string) => {
    if (!window.confirm('Revoke this token? Any machine using it will lose access immediately.')) return;
    setRevokingId(id);
    try {
      await revokeToken(id);
    } finally {
      setRevokingId(null);
    }
  };

  const copyRaw = async () => {
    if (!justCreated) return;
    try {
      await navigator.clipboard.writeText(justCreated.rawToken);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard API can fail in some browsers; user can still copy manually.
    }
  };

  return (
    <div data-tour="api-tokens" className="bg-white dark:bg-slate-900/80 rounded-xl border border-gray-200 dark:border-slate-700/50 overflow-hidden shadow-sm mb-6">
      <div className="px-6 py-4 border-b border-gray-100 dark:border-slate-700/50 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide">
          API Tokens
        </h2>
        <button
          onClick={() => {
            setShowCreate((s) => !s);
            setCreateError(null);
          }}
          className="text-sm font-medium px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
        >
          {showCreate ? 'Cancel' : 'New token'}
        </button>
      </div>

      <div className="p-6 space-y-4">
        <p className="text-sm text-gray-500 dark:text-slate-400">
          Personal Access Tokens are used by the Sente CLI and Claude Code skill to authenticate from
          your terminal. Tokens are scoped to this organization and inherit your role.
        </p>

        {/* Freshly minted token banner -- shown exactly once */}
        {justCreated && (
          <div className="rounded-lg border border-amber-300 dark:border-amber-700/50 bg-amber-50 dark:bg-amber-900/20 px-4 py-3">
            <div className="text-sm font-medium text-amber-900 dark:text-amber-200 mb-2">
              Token created. Copy it now -- it won't be shown again.
            </div>
            <div className="flex items-center gap-2">
              <code className="flex-1 px-3 py-2 text-xs font-mono bg-white dark:bg-slate-900 rounded border border-amber-200 dark:border-amber-800 break-all">
                {justCreated.rawToken}
              </code>
              <button
                onClick={copyRaw}
                className="text-xs font-medium px-3 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg transition-colors"
              >
                {copied ? 'Copied' : 'Copy'}
              </button>
              <button
                onClick={() => setJustCreated(null)}
                className="text-xs font-medium px-3 py-2 bg-gray-200 hover:bg-gray-300 dark:bg-slate-700 dark:hover:bg-slate-600 text-gray-700 dark:text-slate-200 rounded-lg transition-colors"
              >
                Dismiss
              </button>
            </div>
            <div className="mt-3 text-xs text-amber-800 dark:text-amber-300">
              Use it with the CLI: <code className="font-mono">sente login --token {justCreated.rawToken.slice(0, 12)}...</code>
            </div>
          </div>
        )}

        {/* Create form */}
        {showCreate && (
          <form onSubmit={handleCreate} className="rounded-lg border border-gray-200 dark:border-slate-700/50 px-4 py-3">
            <label className="text-sm font-medium text-gray-700 dark:text-slate-200">
              Token name
            </label>
            <p className="text-xs text-gray-500 dark:text-slate-400 mb-2">
              Helps you remember where it's used (e.g. "MacBook CLI", "CI deploy webhook").
            </p>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={newTokenName}
                onChange={(e) => setNewTokenName(e.target.value)}
                placeholder="MacBook CLI"
                maxLength={100}
                autoFocus
                className="flex-1 px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                type="submit"
                disabled={creating || !newTokenName.trim()}
                className="text-sm font-medium px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
              >
                {creating ? 'Creating...' : 'Create token'}
              </button>
            </div>
            {createError && (
              <div className="mt-2 text-xs text-red-600 dark:text-red-400">{createError}</div>
            )}
          </form>
        )}

        {/* Token list */}
        {error && (
          <div className="text-sm text-red-600 dark:text-red-400">{error}</div>
        )}

        {isLoading && tokens.length === 0 ? (
          <div className="text-sm text-gray-500 dark:text-slate-400">Loading...</div>
        ) : tokens.length === 0 ? (
          <div className="text-sm text-gray-500 dark:text-slate-400 italic">
            No tokens yet. Create one to use the Sente CLI -- see{' '}
            <a
              href="https://sente.run/docs/cli"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 dark:text-blue-400 hover:underline"
            >
              install the CLI
            </a>
            .
          </div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-slate-700/50">
            {tokens.map((token: ApiToken) => (
              <div key={token.id} className="flex items-center justify-between py-3">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                    {token.name}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-slate-400 font-mono mt-0.5">
                    {token.prefix}...
                  </div>
                  <div className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">
                    Created {formatRelative(token.createdAt)}
                    {' · '}
                    Last used {token.lastUsedAt ? formatRelative(token.lastUsedAt) : 'never'}
                  </div>
                </div>
                <button
                  onClick={() => handleRevoke(token.id)}
                  disabled={revokingId === token.id}
                  className="text-sm font-medium px-3 py-1.5 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors disabled:opacity-50"
                >
                  {revokingId === token.id ? 'Revoking...' : 'Revoke'}
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="pt-2">
          <button
            onClick={() => refresh()}
            className="text-xs text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-200"
          >
            Refresh
          </button>
        </div>
      </div>
    </div>
  );
};

function formatRelative(iso: string): string {
  const d = new Date(iso);
  const now = Date.now();
  const diff = now - d.getTime();
  const day = 1000 * 60 * 60 * 24;
  if (diff < 60 * 1000) return 'just now';
  if (diff < 60 * 60 * 1000) return `${Math.floor(diff / (60 * 1000))}m ago`;
  if (diff < day) return `${Math.floor(diff / (60 * 60 * 1000))}h ago`;
  if (diff < 7 * day) return `${Math.floor(diff / day)}d ago`;
  return d.toLocaleDateString();
}

export default ApiTokensSection;
